import { useEffect, useState } from 'react';
import type { AnnotationLabel, Customer, DefectType, Health, Product } from '@visionqc/shared';
import { TASKS, TASK_LIST, taskOf } from '@visionqc/shared';
import { api } from '../api/client';
import { Combobox } from '../components/Combobox';
import { useProducts } from '../api/useProducts';
import { HardwarePanel } from '../components/HardwarePanel';
import { NAV_FUNCTIONS } from '../navItems';

export function SettingsView() {
  const { products, refresh } = useProducts();
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    if (selected === null && products.length > 0) setSelected(products[0]!.id);
  }, [products, selected]);

  const current = products.find((p) => p.id === selected) ?? null;

  return (
    <div>
      <h1 className="view-title">Settings</h1>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', alignItems: 'start' }}>
        <div className="grid" style={{ alignContent: 'start' }}>
          <CustomerManager onChanged={refresh} />
          <ProductList
            products={products}
            selected={selected}
            onSelect={setSelected}
            onChanged={refresh}
          />
          {current && (
            <ProductEditor key={current.id} product={current} onChanged={refresh} />
          )}
          {current && <DefectTypeManager product={current} />}
        </div>
        <div className="grid" style={{ alignContent: 'start' }}>
          <HealthPanel />
          <DemoNavMatrix />
          <HardwarePanel />
          <PlanSyncPanel />
        </div>
      </div>
    </div>
  );
}

/**
 * Register the software plans (QC editions + per-line Safety/Jakatori) in the
 * invoicing ledger. Preview is a dry run (shows exactly what would be sent);
 * "Push live" writes to the external ledger — the irreversible, outward step,
 * so it is its own explicit button with a confirm.
 */
function PlanSyncPanel() {
  const [rows, setRows] = useState<Array<{ sku: string; name: string; unitPriceHt: number }> | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const preview = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.syncPlans(false);
      setRows(r.products ?? []);
      setNotice(`Dry run — ${r.wouldPush ?? r.products?.length ?? 0} plans ready to push. Nothing sent yet.`);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const pushLive = async () => {
    if (!confirm('Push the software plans to the live invoicing ledger? This writes products to the external system.')) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.syncPlans(true);
      setNotice(`Pushed live — ${r.pushed ?? 0} plans (${r.created ?? 0} new, ${r.updated ?? 0} updated).`);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="panel">
      <strong>Software plans → invoicing</strong>
      <p className="muted" style={{ fontSize: 12, margin: '4px 0 10px' }}>
        QC editions and the per-line Safety &amp; Jakatori subscriptions, registered as products in the
        invoicing ledger. Preview is a safe dry run; Push live writes to the external system.
      </p>
      {error && <div style={{ color: 'var(--bad)', fontSize: 13, marginBottom: 8 }}>{error}</div>}
      {notice && <div style={{ color: 'var(--good)', fontSize: 13, marginBottom: 8 }}>{notice}</div>}
      <div className="row" style={{ gap: 8 }}>
        <button className="btn sm" disabled={busy} onClick={() => void preview()}>{busy ? '…' : 'Preview (dry run)'}</button>
        <button className="btn sm primary" disabled={busy} onClick={() => void pushLive()}>Push live</button>
      </div>
      {rows && rows.length > 0 && (
        <table style={{ marginTop: 12, width: '100%', fontVariantNumeric: 'tabular-nums' }}>
          <tbody>
            {rows.map((p) => (
              <tr key={p.sku}>
                <td className="muted" style={{ fontSize: 12, fontFamily: 'var(--mono, monospace)' }}>{p.sku}</td>
                <td style={{ fontSize: 13 }}>{p.name}</td>
                <td style={{ textAlign: 'right', fontSize: 13 }}>€{p.unitPriceHt.toLocaleString('nl-NL')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Which sidebar functions the public demo shows. An admin ticks the ones a
 * prospective tenant should meet; the list is stored on the server (a shared
 * file) and the demo reads it. This panel is the real admin's control over the
 * demo — the demo itself never shows it (its nav hides Settings).
 */
function DemoNavMatrix() {
  const [enabled, setEnabled] = useState<Set<string> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/demo-nav')
      .then((r) => r.json())
      .then((d) => setEnabled(new Set(Array.isArray(d?.enabled) ? d.enabled : [])))
      .catch(() => setEnabled(new Set()));
  }, []);

  const toggle = (to: string) => {
    setSaved(false);
    setEnabled((prev) => {
      const n = new Set(prev);
      n.has(to) ? n.delete(to) : n.add(to);
      return n;
    });
  };

  const save = async () => {
    if (!enabled) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/demo-nav', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ enabled: [...enabled] }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Save failed');
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sections = [...new Set(NAV_FUNCTIONS.map((f) => f.section))];

  return (
    <div className="panel">
      <strong>Demo navigation</strong>
      <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
        Tick the functions the public demo (demo.thevisionqc.com) shows in its sidebar — what a
        prospective tenant should see. Unticked functions are hidden there. Production is unaffected.
      </p>
      {enabled === null ? (
        <div className="muted" style={{ fontSize: 13 }}>Loading…</div>
      ) : (
        <>
          {sections.map((section) => (
            <div key={section} style={{ marginTop: 12 }}>
              <div className="stat-label" style={{ marginBottom: 6 }}>{section}</div>
              <div className="grid" style={{ gap: 4, gridTemplateColumns: '1fr 1fr' }}>
                {NAV_FUNCTIONS.filter((f) => f.section === section).map((f) => (
                  <label
                    key={f.to}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0 }}
                  >
                    <input type="checkbox" checked={enabled.has(f.to)} onChange={() => toggle(f.to)} />
                    <span>{f.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="row" style={{ marginTop: 14, gap: 10, alignItems: 'center' }}>
            <button className="btn primary" onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save demo menu'}
            </button>
            {saved && <span style={{ color: 'var(--good)', fontSize: 13 }}>Saved ✓</span>}
            {error && <span style={{ color: 'var(--bad)', fontSize: 13 }}>{error}</span>}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Customers sit above products: a cookie factory has several products, and its
 * datasets and inspections roll up to it — that's what makes the dashboard
 * answerable per customer.
 */
function CustomerManager({ onChanged }: { onChanged: () => void }) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<number | null>(null);
  const load = () => api.listCustomers().then(setCustomers).catch(() => setCustomers([]));

  const rename = async (id: number, next: string) => {
    setEditing(null);
    const trimmed = next.trim();
    const current = customers.find((c) => c.id === id);
    if (!trimmed || trimmed === current?.name) return;
    try {
      await api.updateCustomer(id, { name: trimmed });
      await load();
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const setActive = async (id: number, active: boolean) => {
    try {
      await api.updateCustomer(id, { active });
      await load();
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const add = async () => {
    setError(null);
    try {
      await api.createCustomer({ name });
      setName('');
      void load();
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="panel">
      <strong>Customers</strong>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        Each customer owns products; the dashboard can be filtered to one.
      </p>
      <div className="grid" style={{ gap: 6, margin: '12px 0' }}>
        {/* A customer could be created and then never touched again — no
            rename, no way to retire one. The backend already supported both;
            only the UI was missing. Retiring rather than deleting, because a
            customer's inspection history has to stay readable. */}
        {customers.map((c) => (
          <div
            key={c.id}
            className="chip"
            style={{ justifyContent: 'space-between', opacity: c.active ? 1 : 0.55 }}
          >
            {editing === c.id ? (
              <input
                autoFocus
                defaultValue={c.name}
                onBlur={(e) => void rename(c.id, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void rename(c.id, (e.target as HTMLInputElement).value);
                  if (e.key === 'Escape') setEditing(null);
                }}
                style={{ flex: 1 }}
              />
            ) : (
              <>
                <span>
                  {c.name}
                  {!c.active && <span className="muted"> · retired</span>}
                </span>
                <span className="muted" style={{ fontSize: 11, marginLeft: 'auto' }}>
                  {c.productCount} products · {c.datasetCount} datasets
                </span>
                <button className="btn sm" title="Rename" onClick={() => setEditing(c.id)}>
                  ✎
                </button>
                <button
                  className="btn sm"
                  title={c.active ? 'Retire — hides it from pickers, keeps its history' : 'Bring back'}
                  onClick={() => void setActive(c.id, !c.active)}
                >
                  {c.active ? '⊘' : '↺'}
                </button>
              </>
            )}
          </div>
        ))}
        {customers.length === 0 && <span className="muted">No customers yet.</span>}
      </div>
      <div className="row">
        <input
          placeholder="Customer name (e.g. ACME Cookies)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void add()}
          style={{ flex: 1 }}
        />
        <button className="btn primary sm" onClick={add} disabled={!name}>
          + Add
        </button>
      </div>
      {error && <div style={{ color: 'var(--bad)', marginTop: 8 }}>{error}</div>}
    </div>
  );
}

function ProductList({
  products,
  selected,
  onSelect,
  onChanged,
}: {
  products: Product[];
  selected: number | null;
  onSelect: (id: number) => void;
  onChanged: () => void;
}) {
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [task, setTask] = useState<'anomaly' | 'classification'>('anomaly');
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [err, setErr] = useState<string | null>(null);

  /*
    Asked here because this screen has no customer context — you are looking at
    every product on the site. In the Dataset view the question does not arise:
    you are already inside a customer's dataset, so a product made from a
    selection inherits its owner and nobody is asked twice.
  */
  useEffect(() => {
    api.listCustomers().then(setCustomers).catch(() => setCustomers([]));
  }, []);

  const create = async () => {
    setErr(null);
    try {
      if (customerId === null) {
        setErr('Pick the customer this product belongs to.');
        return;
      }
      const p = await api.createProduct({
        customerId,
        name,
        sku,
        task,
        active: true,
        threshold: 0.5,
        uncertaintyBand: 0,
      });
      setName('');
      setSku('');
      setTask('anomaly');
      setCustomerId(null);
      onChanged();
      onSelect(p.id);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return (
    <div className="panel">
      <strong>Products</strong>
      <div className="grid" style={{ gap: 6, margin: '12px 0' }}>
        {products.map((p) => (
          <div
            key={p.id}
            className={`chip ${selected === p.id ? 'selected' : ''}`}
            style={{ justifyContent: 'space-between' }}
            onClick={() => onSelect(p.id)}
          >
            <span>
              {p.name} <span className="muted">({p.sku})</span>
            </span>
            <span className="row" style={{ gap: 6 }}>
              {/* Shown on every product, not only the unusual one. A label that
                  appears only sometimes is read as a warning rather than as a
                  fact about the line. */}
              <span className="muted" style={{ fontSize: 11 }}>
                {TASKS[taskOf(p.task).id].label}
              </span>
              {!p.active && <span className="muted">inactive</span>}
            </span>
          </div>
        ))}
      </div>
      <div className="row">
        <input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <input placeholder="SKU" value={sku} onChange={(e) => setSku(e.target.value)} />
        <div style={{ minWidth: 220 }}>
          <Combobox
            options={customers.map((c) => ({ value: String(c.id), label: c.name }))}
            value={customerId === null ? null : String(customerId)}
            onChange={(v) => setCustomerId(v ? Number(v) : null)}
            placeholder="Search customers…"
            emptyLabel="— pick a customer —"
            allowEmpty={false}
          />
        </div>
        <button className="btn primary sm" onClick={create} disabled={!name || !sku}>
          + Add
        </button>
      </div>

      {/*
        Which question this line asks.
        
        Here rather than in the editor because it cannot be changed once
        inspections exist — those rows carry a verdict or a class in the shape
        the old task demanded, and switching would make every one of them a lie.
        A choice that is permanent belongs where it is made, not where it is
        edited.
      */}
      <div style={{ marginTop: 10 }}>
        <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
          What does this line ask? Chosen now — it cannot be changed once the product has
          inspections.
        </div>
        <div className="row" style={{ gap: 8 }}>
          {TASK_LIST.map((t) => (
            <button
              key={t.id}
              className={`btn sm${task === t.id ? ' primary' : ''}`}
              onClick={() => setTask(t.id)}
              title={t.summary}
              style={{ textAlign: 'left' }}
            >
              {t.label}
              <span className="muted" style={{ display: 'block', fontSize: 11 }}>
                {t.question}
              </span>
            </button>
          ))}
        </div>
        {task === 'classification' && (
          <div className="muted" style={{ fontSize: 12, marginTop: 8, maxWidth: 520 }}>
            A classification product answers with one of your classes instead of GOOD/BAD/UNSURE.
            After creating it, define the classes it can answer with — the Classes page appears in
            the sidebar as soon as one classification product exists.
          </div>
        )}
      </div>

      {err && <div style={{ color: 'var(--bad)', marginTop: 8 }}>{err}</div>}
    </div>
  );
}

function ProductEditor({ product, onChanged }: { product: Product; onChanged: () => void }) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  useEffect(() => {
    api.listCustomers().then(setCustomers).catch(() => setCustomers([]));
  }, []);
  const [threshold, setThreshold] = useState(product.threshold);
  const [band, setBand] = useState(product.uncertaintyBand);
  const [active, setActive] = useState(product.active);
  const [saving, setSaving] = useState(false);

  const persist = async (
    patch: Partial<Pick<Product, 'threshold' | 'active' | 'uncertaintyBand'>>,
  ) => {
    setSaving(true);
    try {
      await api.updateProduct(product.id, patch);
      onChanged();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="panel">
      <strong>{product.name}</strong>
      <div style={{ marginTop: 12 }}>
        <label>Customer</label>
        <select
          value={product.customerId ?? ''}
          onChange={async (e) => {
            await api.setProductCustomer(product.id, e.target.value ? Number(e.target.value) : null);
            onChanged();
          }}
          style={{ width: '100%' }}
        >
          <option value="">— unassigned —</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div style={{ marginTop: 16 }}>
        <label>
          BAD threshold: <strong style={{ fontFamily: 'var(--mono)' }}>{threshold.toFixed(2)}</strong>{' '}
          <span className="muted">(score ≥ threshold ⇒ BAD)</span>
        </label>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={threshold}
          style={{ width: '100%' }}
          onChange={(e) => setThreshold(Number(e.target.value))}
          onMouseUp={() => persist({ threshold })}
          onTouchEnd={() => persist({ threshold })}
        />
      </div>
      <div style={{ marginTop: 16 }}>
        <label>
          UNSURE band: <strong style={{ fontFamily: 'var(--mono)' }}>±{band.toFixed(2)}</strong>{' '}
          <span className="muted">
            {band === 0
              ? '(off — model always picks GOOD/BAD)'
              : `(scores ${(threshold - band).toFixed(2)}–${(threshold + band).toFixed(2)} => UNSURE)`}
          </span>
        </label>
        <input
          type="range"
          min={0}
          max={0.5}
          step={0.01}
          value={band}
          style={{ width: '100%' }}
          onChange={(e) => setBand(Number(e.target.value))}
          onMouseUp={() => persist({ uncertaintyBand: band })}
          onTouchEnd={() => persist({ uncertaintyBand: band })}
        />
        <p className="muted" style={{ fontSize: 12 }}>
          Lets the model decline to guess on ambiguous images; they go to the front of the
          annotation queue for a human.
        </p>
      </div>
      <label className="row" style={{ gap: 8, marginTop: 12 }}>
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => {
            setActive(e.target.checked);
            void persist({ active: e.target.checked });
          }}
        />
        Active
      </label>
      {saving && <div className="muted" style={{ marginTop: 8 }}>Saving…</div>}
    </div>
  );
}

/**
 * Defect library + per-product applicability.
 *
 * Defect types are one shared catalog (named, colored, each declaring whether it
 * means GOOD/BAD/UNSURE). A product applies the subset that's relevant to it, so
 * ballbearings and cookies draw different vocabularies from the same list
 * instead of duplicating "Scratch" per product.
 */
function DefectTypeManager({ product }: { product: Product }) {
  const [library, setLibrary] = useState<DefectType[]>([]);
  const [applied, setApplied] = useState<DefectType[]>([]);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#ff3b30');
  const [classification, setClassification] = useState<AnnotationLabel>('BAD');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const [lib, app] = await Promise.all([
      api.listDefectLibrary().catch(() => []),
      api.listDefectTypes(product.id).catch(() => []),
    ]);
    setLibrary(lib);
    setApplied(app);
  };
  useEffect(() => {
    void load();
  }, [product.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const appliedIds = new Set(applied.map((d) => d.id));

  const addToLibrary = async () => {
    setError(null);
    try {
      await api.createLibraryDefectType({
        name,
        color,
        classification,
        description: description || null,
        sortOrder: library.length,
      });
      setName('');
      setDescription('');
      void load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const toggle = async (dt: DefectType) => {
    if (appliedIds.has(dt.id)) await api.unapplyDefectType(product.id, dt.id);
    else await api.applyDefectType(product.id, dt.id);
    void load();
  };

  /**
   * Apply the whole library at once.
   *
   * Sequential rather than parallel: these are small writes, and a burst of
   * concurrent applies against the same product is a needless race for no
   * gain in a one-off action.
   */
  const applyAll = async () => {
    for (const dt of library) {
      if (!appliedIds.has(dt.id)) await api.applyDefectType(product.id, dt.id);
    }
    void load();
  };

  const setClass = async (id: number, c: AnnotationLabel) => {
    await api.updateDefectType(id, { classification: c });
    void load();
  };

  const removeFromLibrary = async (id: number) => {
    if (!window.confirm('Remove this defect type from the library for ALL products?')) return;
    await api.deleteLibraryDefectType(id);
    void load();
  };

  return (
    <div className="panel">
      <strong>Defect library</strong>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        One shared list. Tick which entries apply to <strong>{product.name}</strong> — those are
        what operators see while annotating it.
      </p>

      {/* A new product starts with nothing ticked, which reads as "the library
          is empty — I have to invent every defect myself". Say what is actually
          true, and make the bulk case one click. */}
      {appliedIds.size === 0 && library.length > 0 && (
        <div
          className="row"
          style={{
            justifyContent: 'space-between',
            gap: 10,
            marginTop: 10,
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--panel-2)',
          }}
        >
          <span style={{ fontSize: 13 }}>
            No defects apply to <strong>{product.name}</strong> yet — operators will see an empty
            list. Tick the ones that can happen to it, or start with all {library.length}.
          </span>
          <button className="btn sm" onClick={() => void applyAll()}>
            Apply all {library.length}
          </button>
        </div>
      )}

      <div className="grid" style={{ gap: 8, margin: '12px 0' }}>
        {library.map((dt) => {
          const on = appliedIds.has(dt.id);
          return (
            <div
              key={dt.id}
              className={`chip ${on ? 'selected' : ''}`}
              style={{ justifyContent: 'space-between', gap: 10 }}
            >
              <label className="row" style={{ gap: 8, flex: 1, margin: 0, cursor: 'pointer' }}>
                <input type="checkbox" checked={on} onChange={() => void toggle(dt)} />
                <span className="swatch" style={{ background: dt.color }} />
                <span>
                  {dt.name}
                  {dt.description && (
                    <span className="muted" style={{ display: 'block', fontSize: 11 }}>
                      {dt.description}
                    </span>
                  )}
                </span>
              </label>
              <select
                value={dt.classification}
                onChange={(e) => void setClass(dt.id, e.target.value as AnnotationLabel)}
                style={{ padding: '4px 6px', fontSize: 12 }}
              >
                <option value="BAD">BAD</option>
                <option value="GOOD">GOOD</option>
                <option value="UNSURE">UNSURE</option>
              </select>
              <button
                className="btn sm"
                title="Remove from library (all products)"
                onClick={() => void removeFromLibrary(dt.id)}
              >
                ✕
              </button>
            </div>
          );
        })}
        {library.length === 0 && <span className="muted">Library is empty.</span>}
      </div>

      <div className="grid" style={{ gap: 8 }}>
        <div className="row">
          <input placeholder="New defect name" value={name} onChange={(e) => setName(e.target.value)} />
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            style={{ width: 48, padding: 4 }}
            title="Box colour. Boxes are drawn with a dark halo, so this stays visible on dark and light products alike."
          />
          {/* Quick picks incl. white for dark products. */}
          {['#ffffff', '#00e5ff', '#ff3b30', '#fab219'].map((c) => (
            <button
              key={c}
              className="btn sm"
              title={`Use ${c}`}
              onClick={() => setColor(c)}
              style={{ background: c, width: 26, height: 26, padding: 0, border: '1px solid var(--border)' }}
            />
          ))}
          <select
            value={classification}
            onChange={(e) => setClassification(e.target.value as AnnotationLabel)}
          >
            <option value="BAD">means BAD</option>
            <option value="GOOD">means GOOD</option>
            <option value="UNSURE">means UNSURE</option>
          </select>
        </div>
        <div className="row">
          <input
            placeholder="Guidance for the operator (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ flex: 1 }}
          />
          <button className="btn primary sm" onClick={addToLibrary} disabled={!name}>
            + Add to library
          </button>
        </div>
        {error && <div style={{ color: 'var(--bad)' }}>{error}</div>}
      </div>
    </div>
  );
}

function HealthPanel() {
  const [health, setHealth] = useState<Health | null>(null);
  useEffect(() => {
    const tick = () => api.health().then(setHealth).catch(() => setHealth(null));
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, []);

  const Row = ({ label, ok, detail }: { label: string; ok: boolean; detail?: string }) => (
    <div className="row" style={{ justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <span className="row" style={{ gap: 10 }}>
        <span className={`health-dot ${ok ? 'ok' : 'bad'}`} />
        {label}
      </span>
      <span className="muted" style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>
        {detail ?? (ok ? 'ok' : 'down')}
      </span>
    </div>
  );

  return (
    <div className="panel">
      <strong>Service health</strong>
      {!health ? (
        <div className="spinner">Checking…</div>
      ) : (
        <div style={{ marginTop: 12 }}>
          <Row label="Database (TimescaleDB)" ok={health.db.ok} detail={health.db.detail} />
          <Row label="Object storage (MinIO)" ok={health.minio.ok} detail={health.minio.detail} />
          <Row label="Inference service" ok={health.inference.ok} detail={health.inference.detail} />
          <Row
            label="GPU acceleration"
            ok={health.gpu.available}
            detail={health.gpu.available ? `cuda (${health.gpu.device})` : 'cpu fallback'}
          />
          <div className="row" style={{ marginTop: 14 }}>
            <span className={`badge ${health.ok ? 'good' : 'bad'}`}>
              {health.ok ? 'ALL SYSTEMS OK' : 'DEGRADED'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
